import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { supabase } from '../lib/supabase'
import { LoginPage } from './LoginPage'

const REDIRECT_KEY = 'solharbor_redirect_url'

interface ProtectedRouteProps {
  children: React.ReactNode
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { user, loading } = useAuth()
  const navigate = useNavigate()
  const [redirectHandled, setRedirectHandled] = useState(false)

  // Capture redirect param on mount (before login)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const redirectUrl = params.get('redirect')
    if (redirectUrl) {
      sessionStorage.setItem(REDIRECT_KEY, redirectUrl)
      // Clean the URL so the param doesn't persist
      window.history.replaceState({}, '', window.location.pathname)
    }
  }, [])

  // After login, check for stored redirect and navigate to matching app
  useEffect(() => {
    if (!user || redirectHandled) return

    const storedRedirect = sessionStorage.getItem(REDIRECT_KEY)
    if (!storedRedirect) {
      setRedirectHandled(true)
      return
    }

    const handleRedirect = async () => {
      try {
        const redirectOrigin = new URL(storedRedirect).origin

        // Find external apps the user has access to
        const { data: apps } = await supabase
          .from('applications')
          .select(`
            id,
            url,
            app_type,
            user_app_access!inner(user_id)
          `)
          .eq('user_app_access.user_id', user.id)
          .eq('is_active', true)
          .eq('app_type', 'external')

        // Match by origin
        const matchingApp = apps?.find(app => {
          try {
            return new URL(app.url).origin === redirectOrigin
          } catch {
            return false
          }
        })

        sessionStorage.removeItem(REDIRECT_KEY)

        if (matchingApp) {
          navigate(`/app/${matchingApp.id}?deepLink=${encodeURIComponent(storedRedirect)}`)
        }
      } catch {
        // Invalid URL or other error — just clear and continue to dashboard
        sessionStorage.removeItem(REDIRECT_KEY)
      }

      setRedirectHandled(true)
    }

    handleRedirect()
  }, [user, redirectHandled, navigate])

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--solharbor-gray-50)' }}>
        <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: 'var(--solharbor-primary)' }}></div>
      </div>
    )
  }

  if (!user) {
    return <LoginPage />
  }

  // Wait for redirect check to complete before rendering children
  if (!redirectHandled) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--solharbor-gray-50)' }}>
        <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: 'var(--solharbor-primary)' }}></div>
      </div>
    )
  }

  return <>{children}</>
}
